/**
 * Date Helpers - Timezone-safe date parsing and formatting
 *
 * JavaScript's `new Date("2026-01-01")` interprets date-only strings as UTC midnight.
 * When displayed in a local timezone west of UTC (e.g. ET = UTC-5), the date shifts
 * backward by 1 day. These utilities avoid that by parsing date-only strings as local noon.
 *
 * Use these instead of `new Date(dateStr)` for any date-only value from the database or CSV.
 */

/**
 * Parse a date-only string as a local date (not UTC).
 *
 * - "YYYY-MM-DD" → parsed as local noon (avoids UTC midnight shift)
 * - "MM/DD/YYYY" or "M/D/YYYY" → JavaScript already parses as local
 */
export function parseDateOnly(dateStr: string): Date {
  if (!dateStr) return new Date(NaN);

  // ISO date-only format: append T12:00:00 to force local interpretation
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(dateStr + 'T12:00:00');
  }

  return new Date(dateStr);
}

/**
 * Format a date-only string for display (e.g. "Jan 1, 2026").
 * Safe against timezone shift.
 */
export function formatDateSafe(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = parseDateOnly(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Normalize any date string to YYYY-MM-DD without timezone shift.
 * Handles both "MM/DD/YYYY" (ActBlue CSV) and "YYYY-MM-DD" (database) formats.
 */
export function normalizeDateToISO(dateStr: string): string {
  if (!dateStr) return dateStr;
  const d = parseDateOnly(dateStr);
  if (isNaN(d.getTime())) {
    console.warn(`Invalid date: ${dateStr}`);
    return dateStr;
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
